import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Share2, Copy, Check, Loader2, Trash2, Link as LinkIcon,
  Eye, ExternalLink, Calendar, ShieldOff,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import inventoryShareApi from "@/api/inventoryShare";

const SECTIONS = [
  { id: "kpis",                label: "KPIs (resumen general)" },
  { id: "summary_table",       label: "Tabla Resumen por SKU" },
  { id: "monthly_pivot",       label: "Tabla Pivot mensual" },
  { id: "dead_stock",          label: "Tabla Dead stock" },
  { id: "user_movements",      label: "Tabla Por usuario" },
  { id: "top_products",        label: "Tabla Top productos" },
  { id: "charts",              label: "Gráficas (4 charts)" },
  { id: "needs_review_widget", label: "Widget Requieren revisión" },
];

const EXPIRATION_OPTIONS = [
  { value: "7",    labelKey: "inventory.share.exp7",    fallback: "7 días" },
  { value: "30",   labelKey: "inventory.share.exp30",   fallback: "30 días" },
  { value: "90",   labelKey: "inventory.share.exp90",   fallback: "90 días" },
  { value: "none", labelKey: "inventory.share.expNone", fallback: "Sin expiración" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Phase 7.1 — Inventory share-link creator + manager dialog.
 *
 * Two tabs:
 *  - "Crear" — checkboxes per section + expiration + toggle warehouse +
 *    snapshot of current filters. Click "Crear link" → POST, then shows
 *    the public URL with a "Copiar" button.
 *  - "Mis enlaces" — table of links the current user has created, with
 *    section badges, status, view counter, and a revoke button.
 */
export default function ShareInventoryDialog({
  open,
  onClose,
  currentFilters,
  includeNonWarehouse,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("create");
  const [submitting, setSubmitting] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);

  // Create form state
  const [label, setLabel] = useState("");
  const [enabled, setEnabled] = useState(() => new Set(SECTIONS.map((s) => s.id)));
  const [expiration, setExpiration] = useState("7");
  const [shareToggle, setShareToggle] = useState(!!includeNonWarehouse);
  const [resetFilters, setResetFilters] = useState(false);

  // Links list state
  const [links, setLinks] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [revoking, setRevoking] = useState(null);

  // Reset state on open
  useEffect(() => {
    if (!open) return;
    setTab("create");
    setCreatedLink(null);
    setCopied(false);
    setLabel("");
    setEnabled(new Set(SECTIONS.map((s) => s.id)));
    setExpiration("7");
    setShareToggle(!!includeNonWarehouse);
    setResetFilters(false);
  }, [open, includeNonWarehouse]);

  // Fetch list when switching to "mine" tab
  useEffect(() => {
    if (!open || tab !== "mine") return;
    setLoadingList(true);
    inventoryShareApi
      .list()
      .then((r) => setLinks(r.items || []))
      .catch(() => toast.error(t("inventory.share.listError", { defaultValue: "No se pudieron cargar tus enlaces" })))
      .finally(() => setLoadingList(false));
  }, [open, tab, t]);

  const toggleSection = (id) => {
    setEnabled((prev) => {
      const ns = new Set(prev);
      if (ns.has(id)) ns.delete(id);
      else ns.add(id);
      return ns;
    });
  };

  const filterSnapshot = useMemo(() => {
    if (resetFilters) return {};
    return {
      preset: currentFilters?.preset || "year",
      date_from: currentFilters?.date_from || undefined,
      date_to: currentFilters?.date_to || undefined,
      source_report: currentFilters?.source_report || undefined,
      canonical_types: currentFilters?.transaction_types || undefined,
    };
  }, [currentFilters, resetFilters]);

  const submit = async () => {
    if (enabled.size === 0) {
      toast.error(t("inventory.share.needSection", { defaultValue: "Selecciona al menos una sección" }));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        label: label.trim() || null,
        enabledSections: Array.from(enabled),
        defaultFilters: filterSnapshot,
        includeNonWarehouse: shareToggle,
        expiresInDays: expiration === "none" ? null : parseInt(expiration, 10),
      };
      const link = await inventoryShareApi.create(payload);
      setCreatedLink(link);
      toast.success(t("inventory.share.created", { defaultValue: "Enlace creado" }));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error al crear enlace";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!createdLink) return;
    const url = inventoryShareApi.buildPublicUrl(createdLink.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("inventory.share.copied", { defaultValue: "Enlace copiado" }));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar — selecciona el texto manualmente");
    }
  };

  const handleRevoke = async (token) => {
    setRevoking(token);
    try {
      await inventoryShareApi.revoke(token);
      setLinks((prev) =>
        prev.map((l) => (l.token === token ? { ...l, is_revoked: true, status: "revoked" } : l))
      );
      toast.success(t("inventory.share.revoked", { defaultValue: "Enlace revocado" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al revocar");
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[640px]" data-testid="share-inventory-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            {t("inventory.share.title", { defaultValue: "Compartir dashboard" })}
          </DialogTitle>
          <DialogDescription>
            {t("inventory.share.subtitle", {
              defaultValue: "Crea un enlace público para que terceros vean este dashboard sin necesidad de iniciar sesión.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="create" data-testid="share-tab-create">
              {t("inventory.share.tabCreate", { defaultValue: "Crear nuevo" })}
            </TabsTrigger>
            <TabsTrigger value="mine" data-testid="share-tab-mine">
              {t("inventory.share.tabMine", { defaultValue: "Mis enlaces" })}
            </TabsTrigger>
          </TabsList>

          {/* ── Create ──────────────────────────────────────────── */}
          <TabsContent value="create" className="space-y-4 mt-4">
            {!createdLink ? (
              <>
                {/* Label */}
                <div className="space-y-1.5">
                  <Label htmlFor="share-label" className="text-xs">
                    {t("inventory.share.labelField", { defaultValue: "Nombre del enlace (opcional)" })}
                  </Label>
                  <Input
                    id="share-label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="Ej. Dashboard Q1 2026 para Gerencia"
                    className="h-8 text-sm"
                    data-testid="share-label-input"
                  />
                </div>

                {/* Sections */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t("inventory.share.sectionsTitle", { defaultValue: "Secciones a incluir" })}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-border bg-card p-3">
                    {SECTIONS.map((s) => (
                      <label
                        key={s.id}
                        className="flex items-center gap-2 text-sm cursor-pointer hover:text-foreground"
                        data-testid={`share-section-${s.id}`}
                      >
                        <Checkbox
                          checked={enabled.has(s.id)}
                          onCheckedChange={() => toggleSection(s.id)}
                        />
                        <span>{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Filters snapshot */}
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t("inventory.share.filtersTitle", { defaultValue: "Filtros iniciales" })}
                  </div>
                  <div className="rounded-md border border-border bg-card p-3 text-xs space-y-1.5">
                    {resetFilters ? (
                      <div className="text-muted-foreground italic">
                        {t("inventory.share.filtersReset", { defaultValue: "Sin filtros iniciales (el visitante define todos)" })}
                      </div>
                    ) : (
                      <>
                        <div>
                          <span className="text-muted-foreground">Rango:</span>{" "}
                          <span className="font-mono">{currentFilters?.preset || "year"}</span>
                          {currentFilters?.preset === "custom" && (
                            <span className="ml-2 font-mono">
                              {currentFilters?.date_from} → {currentFilters?.date_to}
                            </span>
                          )}
                        </div>
                        {currentFilters?.source_report && (
                          <div>
                            <span className="text-muted-foreground">Fuente:</span>{" "}
                            <span className="font-mono">{currentFilters.source_report}</span>
                          </div>
                        )}
                      </>
                    )}
                    <label className="flex items-center gap-2 text-xs cursor-pointer mt-2 pt-2 border-t border-border">
                      <Checkbox
                        checked={resetFilters}
                        onCheckedChange={(v) => setResetFilters(!!v)}
                        data-testid="share-reset-filters"
                      />
                      <span>{t("inventory.share.resetDefaults", { defaultValue: "Resetear a defaults (sin filtros)" })}</span>
                    </label>
                  </div>
                </div>

                {/* Toggle warehouse */}
                <div className="flex items-center justify-between rounded-md border border-border bg-card p-3">
                  <Label htmlFor="share-non-wh" className="text-xs cursor-pointer">
                    {t("inventory.share.nonWarehouse", { defaultValue: "Incluir agregados de todos los usuarios" })}
                  </Label>
                  <Switch
                    id="share-non-wh"
                    checked={shareToggle}
                    onCheckedChange={setShareToggle}
                    data-testid="share-non-warehouse-toggle"
                  />
                </div>

                {/* Expiration */}
                <div className="space-y-1.5">
                  <Label htmlFor="share-exp" className="text-xs">
                    {t("inventory.share.expirationTitle", { defaultValue: "Expiración" })}
                  </Label>
                  <Select value={expiration} onValueChange={setExpiration}>
                    <SelectTrigger id="share-exp" className="h-8 text-sm" data-testid="share-expiration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRATION_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {t(o.labelKey, { defaultValue: o.fallback })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              /* ── Result panel ──────────────────────────────── */
              <div className="space-y-3" data-testid="share-result-panel">
                <div className="flex items-center gap-2 text-emerald-600 text-sm">
                  <Check className="h-4 w-4" />
                  {t("inventory.share.successHeading", { defaultValue: "¡Enlace creado!" })}
                </div>
                <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
                  <div className="text-xs text-muted-foreground">{t("inventory.share.urlLabel", { defaultValue: "URL pública" })}</div>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={inventoryShareApi.buildPublicUrl(createdLink.token)}
                      className="font-mono text-xs h-8"
                      data-testid="share-result-url"
                      onClick={(e) => e.target.select()}
                    />
                    <Button
                      size="sm"
                      onClick={handleCopy}
                      variant={copied ? "default" : "outline"}
                      data-testid="share-copy-btn"
                    >
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                    <a
                      href={inventoryShareApi.buildPublicUrl(createdLink.token)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="share-open-btn"
                    >
                      <Button size="sm" variant="outline">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {createdLink.enabled_sections.map((s) => (
                      <Badge key={s} variant="secondary" className="text-[10px]">
                        {SECTIONS.find((x) => x.id === s)?.label || s}
                      </Badge>
                    ))}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {createdLink.expires_at
                      ? `${t("inventory.share.expiresAt", { defaultValue: "Caduca el" })} ${fmtDate(createdLink.expires_at)}`
                      : t("inventory.share.noExpire", { defaultValue: "Sin expiración" })}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Mine ────────────────────────────────────────────── */}
          <TabsContent value="mine" className="mt-4">
            {loadingList ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                {t("common.loading", { defaultValue: "Cargando…" })}
              </div>
            ) : links.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm" data-testid="share-mine-empty">
                <LinkIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                {t("inventory.share.empty", { defaultValue: "Aún no has creado enlaces." })}
              </div>
            ) : (
              <div className="rounded-md border border-border overflow-hidden max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Token / Label</TableHead>
                      <TableHead className="text-xs">Estado</TableHead>
                      <TableHead className="text-xs">Secciones</TableHead>
                      <TableHead className="text-xs text-center">Visitas</TableHead>
                      <TableHead className="text-xs">Expira</TableHead>
                      <TableHead className="text-xs w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {links.map((l) => (
                      <TableRow key={l.token} data-testid={`share-row-${l.token}`}>
                        <TableCell className="text-xs">
                          <div className="font-mono">{l.token.slice(0, 8)}…</div>
                          {l.label && (
                            <div className="text-[10px] text-muted-foreground line-clamp-1">{l.label}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={l.status === "active" ? "default" : "secondary"}
                            className={
                              l.status === "active"
                                ? "bg-emerald-600/80 hover:bg-emerald-600 text-white text-[10px]"
                                : l.status === "expired"
                                ? "bg-amber-600/80 text-white text-[10px]"
                                : "bg-rose-600/80 text-white text-[10px]"
                            }
                          >
                            {l.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          <span className="text-muted-foreground">{l.enabled_sections.length} / {SECTIONS.length}</span>
                        </TableCell>
                        <TableCell className="text-center text-xs tabular-nums">
                          <span className="inline-flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {l.view_count}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">
                          {l.expires_at ? (
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {fmtDate(l.expires_at).split(",")[0]}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">∞</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <a
                              href={inventoryShareApi.buildPublicUrl(l.token)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button size="icon" variant="ghost" className="h-7 w-7">
                                <ExternalLink className="h-3 w-3" />
                              </Button>
                            </a>
                            {!l.is_revoked && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-rose-500 hover:text-rose-700"
                                onClick={() => handleRevoke(l.token)}
                                disabled={revoking === l.token}
                                data-testid={`share-revoke-${l.token}`}
                              >
                                {revoking === l.token ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <ShieldOff className="h-3 w-3" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 sm:gap-0">
          {tab === "create" && !createdLink && (
            <Button
              onClick={submit}
              disabled={submitting || enabled.size === 0}
              data-testid="share-submit-btn"
            >
              {submitting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <LinkIcon className="h-3.5 w-3.5 mr-1.5" />}
              {t("inventory.share.submit", { defaultValue: "Crear link" })}
            </Button>
          )}
          {createdLink && (
            <Button variant="outline" onClick={() => setCreatedLink(null)} data-testid="share-another-btn">
              {t("inventory.share.another", { defaultValue: "Crear otro" })}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            {t("common.close", { defaultValue: "Cerrar" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
